import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import StepContainer from "../components/StepContainer";
import BudgetSection from "../components/BudgetSection";
import { usePaymentValidation } from "../hooks/usePaymentValidation";
import CreativeDataInput from "../components/payment/CreativeDataInput";
import ValidationWarning from "../components/payment/ValidationWarning";
import { useEffect, useState, ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import InvoicePreviewDialog from "../components/InvoicePreviewDialog";
import QuotationButton from "../components/payment/QuotationButton";
import { useQuotationGeneration } from "@/hooks/campaign/useQuotationGeneration";

interface PaymentStepProps {
  devices: number;
  duration: number;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  mediaOwner?: string;
  selectedLocations: any[];
  onResetToFirstStep?: () => void;
  aiAssistantButton?: ReactNode;
}

const PaymentStep = ({ 
  devices, 
  duration, 
  onSubmit, 
  onBack, 
  isSubmitting,
  mediaOwner = 'TableAds',
  selectedLocations = [],
  onResetToFirstStep,
  aiAssistantButton
}: PaymentStepProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isFormValid } = usePaymentValidation(selectedLocations, duration, onResetToFirstStep);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const { toast } = useToast();
  const {
    invoiceData,
    showInvoice,
    setShowInvoice,
    isGeneratingInvoice,
    handleViewQuotation,
    downloadInvoice
  } = useQuotationGeneration();

  // Check if form is valid and update warning state
  useEffect(() => {
    setShowValidationWarning(!isFormValid);
  }, [isFormValid]);

  // Ensure budget calculations are saved
  useEffect(() => {
    // Calculate budget based on devices and duration
    const basePrice = devices * duration * 10; // Basic calculation
    const totalBudget = basePrice; // Can add discounts, taxes etc.
    
    // Store budget information for payment processing
    const budgetCalculations = {
      basePrice,
      totalBudget
    };
    
    localStorage.setItem('budgetCalculations', JSON.stringify(budgetCalculations));
  }, [devices, duration]);

  // Submit handler with enhanced debugging and direct action
  const handleFormSubmit = () => {
    if (!isFormValid) {
      setShowValidationWarning(true);
      toast({
        title: "Validation Failed",
        description: "Please check your form for errors and try again.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Create and dispatch a direct custom event to bypass any event issues
      const submitEvent = new CustomEvent('payment-form-submit', { bubbles: true });
      document.dispatchEvent(submitEvent);
      
      // Direct call to onSubmit
      onSubmit();
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error submitting your campaign. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Listen for custom events
  useEffect(() => {
    const handleDirectSubmit = () => {
      if (isFormValid && !isSubmitting) {
        onSubmit();
      }
    };
    
    document.addEventListener('payment-form-submit', handleDirectSubmit);
    return () => {
      document.removeEventListener('payment-form-submit', handleDirectSubmit);
    };
  }, [onSubmit, isFormValid, isSubmitting]);

  return (
    <StepContainer
      title={t("reviewAndPayment")}
      description={t("reviewCampaignDesc")}
      onNext={handleFormSubmit}
      onBack={onBack}
      isNextDisabled={isSubmitting || !isFormValid}
      nextButtonText={t("submitCampaign")}
      isLastStep={true}
      aiAssistantButton={aiAssistantButton}
    >
      {/* Show validation warning when validation fails */}
      {showValidationWarning && <ValidationWarning />}
      
      {/* Hidden input to help with creative debugging */}
      <CreativeDataInput creativeUrls={localStorage.getItem('creativeUrls') || '[]'} />
      
      <BudgetSection 
        devices={devices} 
        duration={duration} 
        mediaOwner={mediaOwner} 
        selectedLocations={selectedLocations} 
        data-budget-total={localStorage.getItem('budgetCalculations')}
      />

      <div className="mt-6 flex justify-center [&>button]:w-full sm:[&>button]:w-auto">
        <QuotationButton 
          onViewQuotation={handleViewQuotation}
          isGenerating={isGeneratingInvoice}
        />
      </div>

      <InvoicePreviewDialog
        open={showInvoice}
        onOpenChange={setShowInvoice}
        invoiceData={invoiceData}
        onDownload={downloadInvoice}
      />
    </StepContainer>
  );
};

export default PaymentStep;
